import { create } from 'zustand';

/**
 * The product's transient messages — "Class 8 is set up", "That role could not
 * be deleted".
 *
 * A store rather than a provider, so any screen can raise one without threading
 * a context through the tree, and so a message survives the component that
 * raised it being unmounted — which is exactly what happens when a dialog
 * closes on save.
 *
 * Use a toast for the *outcome of an action*. Anything the user has to act on —
 * a field that failed validation, a list that would not load — stays on the
 * page, because a message that fades cannot be the only place an error is told.
 */

export type ToastTone = 'success' | 'critical' | 'info';

export interface ToastMessage {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Milliseconds before it fades on its own. `0` keeps it until dismissed. */
  duration: number;
}

export interface ShowToastInput {
  tone?: ToastTone;
  title: string;
  description?: string;
  duration?: number;
}

/** Long enough to read a sentence; failures get longer, as they matter more. */
const DEFAULT_DURATION: Record<ToastTone, number> = {
  success: 4000,
  info: 5000,
  critical: 8000,
};

/** More than this on screen and the newest one is buried by the stack. */
const MAX_VISIBLE = 3;

interface ToastState {
  toasts: ToastMessage[];
  show: (input: ShowToastInput) => string;
  dismiss: (id: string) => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  show: ({ tone = 'info', title, description, duration }) => {
    const id = crypto.randomUUID();

    set((state) => ({
      toasts: [
        ...state.toasts,
        { id, tone, title, description, duration: duration ?? DEFAULT_DURATION[tone] },
      ].slice(-MAX_VISIBLE),
    }));

    return id;
  },

  dismiss: (id) =>
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),
}));

/**
 * Raise a toast from anywhere — including outside a component, which is why it
 * reads the store imperatively rather than being a hook.
 *
 *   toast.success('Class 8 is set up', '65 seats across 2 sections');
 */
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().show({ tone: 'success', title, description }),

  error: (title: string, description?: string) =>
    useToastStore.getState().show({ tone: 'critical', title, description }),

  info: (title: string, description?: string) =>
    useToastStore.getState().show({ tone: 'info', title, description }),

  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};
